import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Clock, 
  ArrowRight, 
  CheckCircle2, 
  AlertTriangle,
  GraduationCap,
  Users,
  ListTodo,
  HelpCircle,
  FileText,
  CheckSquare
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const Chalkup = () => {
  const faqData = [
    {
      question: "What was Chalkup?",
      answer: "Chalkup was a classroom collaboration platform founded in 2013 that allowed teachers to distribute assignments, grade with rubrics, and foster student discussions. It was used in 1 in 5 high schools and 1 in 3 universities in the US."
    },
    {
      question: "Why did Chalkup shut down?",
      answer: "In February 2018, Microsoft acquired Chalkup's technology and hired its founder Justin Chando. The platform ceased operations on June 30, 2018, with its features being integrated into Microsoft Teams for Education."
    },
    {
      question: "Is CSW a good Chalkup alternative?",
      answer: "Yes! While CSW isn't a full LMS, it provides free collaborative study spaces, task management, and focus tools that support the same collaborative learning philosophy Chalkup championed."
    },
    {
      question: "Can I use CSW for group projects?",
      answer: "Absolutely. CSW's virtual study rooms let project teams collaborate in real-time with video, chat, task lists, and focus timers - perfect for student group work."
    },
    {
      question: "What features did Chalkup have?",
      answer: "Chalkup featured assignment distribution, rubric-based grading, class discussion boards, Google Drive integration, and tools for teachers to send messages and feedback to students."
    },
    {
      question: "Where did Chalkup users go after it shut down?",
      answer: "Many users migrated to Microsoft Teams for Education, which incorporated Chalkup's rubric grading feature. Others use Google Classroom, Canvas, or combination of tools including CSW for collaborative study."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Chalkup Alternative - Free Collaborative Study Platform",
    "description": "Looking for a Chalkup alternative? Chalkup was acquired by Microsoft in 2018. CSW offers free collaborative study rooms, task management, and focus tools for students.",
    "url": "https://chillstudy.work/chalkup/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Chalkup",
      "applicationCategory": "EducationApplication",
      "operatingSystem": "Web"
    },
    "mentions": {
      "@type": "SoftwareApplication",
      "name": "Chill Study Work",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web Browser"
    },
    "keywords": "Chalkup alternative, Chalkup replacement, Chalkup shut down, classroom collaboration platform, student collaboration tool"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1000",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Virtual study rooms",
      "Focus timer",
      "Task management",
      "Video collaboration",
      "Study communities",
      "Reminders",
      "AI-powered flashcards",
      "Progress tracking"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Chalkup Alternative - Free Collaborative Study Platform | CSW</title>
        <meta name="description" content="Looking for a Chalkup alternative? Chalkup was acquired by Microsoft in 2018. CSW offers free collaborative study rooms, task management, and focus tools for students." />
        <meta name="keywords" content="Chalkup alternative, Chalkup replacement, Chalkup shut down, classroom collaboration, student collaboration, Microsoft Teams alternative, educational platform" />
        <link rel="canonical" href="https://chillstudy.work/chalkup/" />
        <meta property="og:title" content="Chalkup Alternative - Free Collaborative Study Platform" />
        <meta property="og:description" content="Chalkup was acquired by Microsoft. CSW offers free collaborative study rooms and productivity tools for students." />
        <meta property="og:url" content="https://chillstudy.work/chalkup/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6">
              <AlertTriangle className="w-4 h-4" />
              Chalkup was acquired by Microsoft • June 30, 2018
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Chalkup Alternative
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Chalkup was acquired by Microsoft in 2018 and its features were integrated into Teams. 
              Looking for collaborative learning tools? <Link to="/study-with-me/" className="text-primary hover:underline">CSW</Link> offers 
              free virtual study rooms, <Link to="/task-management/" className="text-primary hover:underline">task management</Link>, and 
              focus tools for students and educators.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8">
                <Link to="/chalkup/history/">
                  Read Chalkup History
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* What Happened Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 rounded-lg bg-orange-500/10 text-orange-500">
                  <GraduationCap className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">What Happened to Chalkup?</h2>
                  <p className="text-muted-foreground">
                    Chalkup was a beloved classroom collaboration platform founded in 2013 by Justin Chando. 
                    It was used in 1 in 5 US high schools and 1 in 3 universities, known for its rubric-based 
                    grading and student discussion features. In February 2018, Microsoft acquired Chalkup's 
                    technology, hiring Chando to lead classroom features in Microsoft Teams for Education. 
                    Chalkup ceased operations on June 30, 2018.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">2013</div>
                  <div className="text-sm text-muted-foreground">Founded by Justin Chando</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-primary mb-1">1 in 3</div>
                  <div className="text-sm text-muted-foreground">US universities used it</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-destructive mb-1">June 2018</div>
                  <div className="text-sm text-muted-foreground">Shut down after Microsoft acquisition</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why CSW Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Why CSW is the Perfect Alternative</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Users className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Collaborative Learning</h3>
                <p className="text-muted-foreground">
                  Just like Chalkup fostered classroom discussions, CSW provides virtual study rooms where 
                  students collaborate in real-time with <Link to="/group-study/" className="text-primary hover:underline">video, chat, and shared goals</Link>.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <ListTodo className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Assignment Tracking</h3>
                <p className="text-muted-foreground">
                  While CSW isn't a full LMS, its <Link to="/task-management/" className="text-primary hover:underline">task management</Link> and 
                  <Link to="/reminders/" className="text-primary hover:underline"> reminders</Link> help students stay organized with their coursework.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <Clock className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Focus Tools</h3>
                <p className="text-muted-foreground">
                  Our <Link to="/study-timer/" className="text-primary hover:underline">Pomodoro timer</Link> and focus modes 
                  help students maintain concentration during study sessions.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <FileText className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Flashcards & Quizzes</h3>
                <p className="text-muted-foreground">
                  Create AI-powered <Link to="/flash-cards/" className="text-primary hover:underline">flashcards</Link> and 
                  <Link to="/quizzes/" className="text-primary hover:underline"> quizzes</Link> to test your knowledge.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <CheckCircle2 className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Free Forever</h3>
                <p className="text-muted-foreground">
                  Unlike many educational platforms that charge per user, CSW is completely free with unlimited 
                  access for all students.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <CheckSquare className="w-8 h-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">No School Setup Required</h3>
                <p className="text-muted-foreground">
                  Start using CSW immediately without waiting for IT approval. Perfect for informal study groups 
                  and <Link to="/study-with-friends/" className="text-primary hover:underline">studying with friends</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 border border-border/50">
              <div className="flex items-center gap-3 mb-6">
                <HelpCircle className="w-8 h-8 text-primary" />
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* External References */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Learn More About Chalkup</h2>
            <div className="space-y-3">
              <a 
                href="https://www.edsurge.com/news/2018-02-13-chalkup-a-class-collaboration-tool-will-close-shop-and-become-part-of-microsoft-teams" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>EdSurge: Chalkup Joins Microsoft Teams</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
              <a 
                href="https://techcrunch.com/2018/02/13/microsoft-acquires-classroom-collaboration-startup-chalkup-to-expand-microsoft-teams/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>TechCrunch: Microsoft Acquires Chalkup</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Collaborating?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join thousands of students who found their new study home after Chalkup. CSW is free, 
              easy to use, and designed for collaborative learning.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Try CSW Free Today
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Chalkup" />

      <Footer />
    </div>
  );
};

export default Chalkup;
